/**
 * Trustee identity-verification (KYC) hook.
 *
 * Drives one Stripe Identity verification for a given vault + purpose:
 *   1. `startIdentityVerification` mints a provider session and returns a
 *      short-lived `client_secret`.
 *   2. Stripe.js opens the hosted document + selfie modal (`verifyIdentity`).
 *   3. The provider webhook — never the browser — writes the authoritative
 *      outcome, so we poll `getIdentityVerificationStatus` until it settles.
 *
 * The hook holds no key material and is a pure authorization signal; it lives
 * entirely outside the threshold-crypto path (see docs/TRUSTEE_IDENTITY_VERIFICATION.md).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  getIdentityVerificationStatus,
  startIdentityVerification,
  type IdentityVerificationPurpose,
} from "@secure-vault/api";
import { getStripe } from "@/lib/stripe";

export type IdentityStatus =
  | "unknown" // not yet loaded
  | "none" // no verification attempted
  | "pending" // session created / awaiting provider result
  | "verified"
  | "failed"
  | "expired";

const TERMINAL: IdentityStatus[] = ["verified", "failed", "expired"];

// Poll budget after the Stripe modal closes — the webhook typically lands within
// a few seconds, but we allow generous headroom before giving up on "pending".
const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 120_000;

interface UseIdentityVerificationArgs {
  clientId: string;
  purpose: IdentityVerificationPurpose;
  /** Required when `purpose === "unlock"`: the unlock session being gated. */
  unlockSessionId?: string;
  /** Skip the initial status fetch until the caller has the ids it needs. */
  enabled?: boolean;
}

interface UseIdentityVerificationResult {
  status: IdentityStatus;
  /** Initial status load in flight. */
  loading: boolean;
  /** A verify() flow (modal + polling) is in progress. */
  running: boolean;
  error: string | null;
  /** Re-fetch the latest status from the server. */
  refresh: () => Promise<void>;
  /** Launch the Stripe modal and poll to a terminal status. Returns the result. */
  verify: () => Promise<IdentityStatus>;
}

export function useIdentityVerification({
  clientId,
  purpose,
  unlockSessionId,
  enabled = true,
}: UseIdentityVerificationArgs): UseIdentityVerificationResult {
  const [status, setStatus] = useState<IdentityStatus>("unknown");
  const [loading, setLoading] = useState<boolean>(false);
  const [running, setRunning] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Guard against setState after unmount during long polls.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const res = await getIdentityVerificationStatus(clientId, purpose);
      if (mounted.current) setStatus((res.status as IdentityStatus) ?? "none");
    } catch (err: unknown) {
      // A 403 here just means "no role / not yet resolvable" — treat as none.
      if (mounted.current) setStatus("none");
      console.warn("Failed to load identity status:", err);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [clientId, purpose]);

  useEffect(() => {
    if (enabled && clientId) void refresh();
  }, [enabled, clientId, refresh]);

  const verify = useCallback(async (): Promise<IdentityStatus> => {
    setError(null);

    if (purpose === "unlock" && !unlockSessionId) {
      const msg = "Please open the latest link sent to you and try again.";
      setError(msg);
      return status;
    }

    const stripe = await getStripe()?.catch(() => null);
    if (!stripe) {
      const msg =
        "The identity check is unavailable right now. Try again later.";
      setError(msg);
      return status;
    }

    setRunning(true);
    try {
      // 1. Mint a provider session.
      const { client_secret } = await startIdentityVerification({
        client_id: clientId,
        purpose,
        unlock_session_id: purpose === "unlock" ? unlockSessionId : undefined,
      });
      if (mounted.current) setStatus("pending");

      // 2. Open Stripe's hosted document + selfie modal. Resolves when the user
      //    finishes or dismisses it; `result.error` is set only on hard errors.
      const result = await stripe.verifyIdentity(client_secret);
      if (result.error) {
        // User dismissed or a client-side error — surface but keep any prior status.
        const msg = "The identity check was not completed.";
        if (mounted.current) setError(msg);
        // Fall through to a status refresh in case the session still progressed.
      }

      // 3. Poll for the authoritative webhook-written result.
      const deadline = Date.now() + POLL_TIMEOUT_MS;
      // Give the webhook a moment before the first poll.
      for (;;) {
        await sleep(POLL_INTERVAL_MS);
        if (!mounted.current) return status;
        let current: IdentityStatus = "pending";
        try {
          const res = await getIdentityVerificationStatus(clientId, purpose);
          current = (res.status as IdentityStatus) ?? "pending";
        } catch {
          current = "pending";
        }
        if (mounted.current) setStatus(current);
        if (TERMINAL.includes(current)) return current;
        if (Date.now() > deadline) return current;
      }
    } catch (err: unknown) {
      console.error("Failed to confirm trustee identity:", err);
      const msg = "We couldn't start the identity check. Please try again.";
      if (mounted.current) setError(msg);
      return status;
    } finally {
      if (mounted.current) setRunning(false);
    }
  }, [clientId, purpose, unlockSessionId, status]);

  return { status, loading, running, error, refresh, verify };
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
